import type { VariantProp } from './colors';
import { colors, css, darkThemeSelector, keyframes } from '../stitches.config';

export const waveFrames = keyframes({
  '0%': {
    backgroundPosition: '-200% 50%',
  },
  '100%': {
    backgroundPosition: '200% 50%',
  },
});

export const waveInitialColor = '$$waveInitialColor';
export const waveStageColor = '$$waveStageColor';

export const getWaveVariantStyles = (
  variant: VariantProp,
  application: 'color' | 'background' = 'color',
) => {
  const colorVariantColors = {
    alternative: {
      initial: colors.blue500,
      stage: colors.blue800,
      initialDark: colors.blue300,
      stageDark: colors.blue50,
    },
    attention: {
      initial: colors.amber500,
      stage: colors.amber800,
      initialDark: colors.amber300,
      stageDark: colors.amber50,
    },
    brand: {
      initial: colors.brand500,
      stage: colors.brand800,
      initialDark: colors.brand300,
      stageDark: colors.brand50,
    },
    negative: {
      initial: colors.red500,
      stage: colors.red800,
      initialDark: colors.red300,
      stageDark: colors.red50,
    },
    neutral: {
      initial: colors.gray500,
      stage: colors.gray800,
      initialDark: colors.gray300,
      stageDark: colors.gray50,
    },
    positive: {
      initial: colors.green500,
      stage: colors.green800,
      initialDark: colors.green300,
      stageDark: colors.green50,
    },
  };

  const backgroundVariantColors = {
    alternative: {
      initial: colors.blue50,
      stage: colors.blue100,
      initialDark: colors.blue900,
      stageDark: colors.blue800,
    },
    attention: {
      initial: colors.amber50,
      stage: colors.amber100,
      initialDark: colors.amber900,
      stageDark: colors.amber800,
    },
    brand: {
      initial: colors.brand50,
      stage: colors.brand100,
      initialDark: colors.brand900,
      stageDark: colors.brand800,
    },
    negative: {
      initial: colors.red50,
      stage: colors.red100,
      initialDark: colors.red900,
      stageDark: colors.red800,
    },
    neutral: {
      initial: colors.gray50,
      stage: colors.gray100,
      initialDark: colors.gray900,
      stageDark: colors.gray800,
    },
    positive: {
      initial: colors.green50,
      stage: colors.green100,
      initialDark: colors.green900,
      stageDark: colors.green800,
    },
  };

  const variantColors = application === 'background' ? backgroundVariantColors : colorVariantColors;
  const variantColor = variantColors[variant];

  return {
    [waveInitialColor]: variantColor.initial,
    [waveStageColor]: variantColor.stage,

    [darkThemeSelector]: {
      [waveInitialColor]: variantColor.initialDark,
      [waveStageColor]: variantColor.stageDark,
    },
  };
};

export const getWaveStyles = (
  variant: VariantProp = 'neutral',
  application: 'color' | 'background' = 'color',
) => ({
  background: `linear-gradient(
      90deg,
      ${waveInitialColor} 0%,
      ${waveStageColor} 25%,
      ${waveInitialColor} 50%,
      ${waveStageColor} 75%,
      ${waveInitialColor} 100%
    )`,
  backgroundSize: '200% 100%',
  animation: `${waveFrames} 4s linear infinite`,
  ...(application === 'color' && {
    fontWeight: '$bold',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    color: 'transparent',
  }),
  ...getWaveVariantStyles(variant, application),
});

export const wave = css({
  background: `linear-gradient(
    90deg,
    ${waveInitialColor} 0%,
    ${waveStageColor} 25%,
    ${waveInitialColor} 50%,
    ${waveStageColor} 75%,
    ${waveInitialColor} 100%
  )`,
  backgroundSize: '200% 100%',
  animation: `${waveFrames} 4s linear infinite`,

  variants: {
    application: {
      background: {},
      color: {
        fontWeight: '$bold',
        backgroundClip: 'text',
        WebkitBackgroundClip: 'text',
        color: 'transparent',
      },
    },
    variant: {
      alternative: {
        ...getWaveVariantStyles('alternative', 'color'),
      },
      attention: {
        ...getWaveVariantStyles('attention', 'color'),
      },
      brand: {
        ...getWaveVariantStyles('brand', 'color'),
      },
      negative: {
        ...getWaveVariantStyles('negative', 'color'),
      },
      neutral: {
        ...getWaveVariantStyles('neutral', 'color'),
      },
      positive: {
        ...getWaveVariantStyles('positive', 'color'),
      },
    },
  },

  defaultVariants: {
    application: 'color',
    variant: 'neutral',
  },
});
